import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";

const Ligne = ({ label, value, testId, fort }) => (
  <div className="flex items-baseline justify-between gap-4 py-1 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm tabular text-right ${fort ? "font-semibold" : ""}`} data-testid={testId}>{value ?? "—"}</span>
  </div>
);

const Bloc = ({ titre, children }) => (
  <div className="rounded-lg border border-slate-200 p-3">
    <h4 className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{titre}</h4>
    {children}
  </div>
);

const Liens = ({ d }) => (
  <Bloc titre="Dépôt Desjardins et facture de commission">
    {d.depots.length ? d.depots.map((t) => (
      <Ligne key={t.id} label={`Dépôt du ${t.date}${t.nb_reservations > 1 ? ` (groupé, ${t.nb_reservations} réservations)` : ""}`} value={money(t.montant)} testId="resa-detail-depot" />
    )) : <p className="text-xs text-amber-700 py-1" data-testid="resa-detail-sans-depot">Aucun dépôt Desjardins rapproché.</p>}
    {d.factures.map((f, i) => (
      <Ligne key={i} label={`Facture Booking n° ${f.numeros.join(", ") || "—"} (${f.mois || ""}, ${f.nb_reservations} rés.)${f.paiement ? ` · payée le ${f.paiement.date}` : " · paiement non trouvé"}`}
        value={money(f.paiement ? f.paiement.montant : f.commission)} testId={`resa-detail-facture-${i}`} />
    ))}
  </Bloc>
);

const Contenu = ({ d }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <Bloc titre="Séjour">
      <Ligne label="Client" value={d.nom_client} testId="resa-detail-client" />
      <Ligne label="Dates" value={d.date_debut ? `${d.date_debut} → ${d.date_fin} (${d.nuits || 0} nuit${d.nuits > 1 ? "s" : ""})` : "—"} />
      <Ligne label="Logement" value={d.logement} testId="resa-detail-logement" />
      <Ligne label="Réservée le" value={d.date_reservation} />
      <Ligne label="Statut" value={d.annulee ? "Annulée" : <StatusBadge statut={d.statut} plateforme={d.plateforme} />} testId="resa-detail-statut" />
    </Bloc>
    <Bloc titre="Montants">
      <Ligne label="Loyer" value={money(d.loyer)} />
      <Ligne label="Ménage" value={money(d.frais_menage)} />
      <Ligne label={`Commission${d.commission_regle ? ` (${d.commission_regle})` : ""}`} value={money(d.commission)} testId="resa-detail-commission" />
      <Ligne label="Taxe de séjour" value={money(d.taxe_hebergement)} />
      <Ligne label={`Montant versé${d.total_estime ? " (estimé)" : ""}`} value={money(d.total_verse)} testId="resa-detail-verse" fort />
    </Bloc>
    <Bloc titre="TPS / TVQ">
      <Ligne label="Base TPS/TVQ" value={money(d.base_tps_tvq)} />
      <Ligne label="TPS attendue" value={money(d.tps_attendue)} testId="resa-detail-tps" />
      <Ligne label="TVQ attendue" value={money(d.tvq_attendue)} testId="resa-detail-tvq" />
      <Ligne label="Taxes versées par la plateforme" value={money(d.taxes_versees)} />
      <Ligne label="Écart" value={money(d.ecart)} fort />
      {d.note_comptable && <p className="text-xs text-slate-500 pt-1">{d.note_comptable}</p>}
    </Bloc>
    <Liens d={d} />
  </div>
);

export const ReservationDetailDialog = ({ rid, initial, onClose }) => {
  const [d, setD] = useState(initial || null);
  useEffect(() => {
    if (initial) setD(initial);
    else if (rid) {
      setD(null);
      api.get(`/reservations/${rid}/detail`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
    }
  }, [rid, initial, onClose]);
  return (
    <Dialog open={!!rid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="resa-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="resa-detail-code">{d ? `${d.plateforme} · ${d.code}` : "Réservation"}</DialogTitle>
          <DialogDescription>{d?.adresse_logement || "Détail de la réservation"}</DialogDescription>
        </DialogHeader>
        {d ? <Contenu d={d} /> : <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />}
      </DialogContent>
    </Dialog>
  );
};
